import type { Environment, LogLevel } from "@ir/core/logger/decorator";
import { StudioFeatures, type StudioFeatureKey } from "@intelliextract-studio/shared";

function parseCostFractionDigits(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 8) {
    return fallback;
  }
  return parsed;
}

export class WebAppConfig {
  private static instance: WebAppConfig;

  public readonly graphqlBaseUrl: string;
  public readonly apiBaseUrl: string;
  public readonly auth0Domain: string;
  public readonly auth0ClientId: string;
  public readonly auth0Audience: string;
  public readonly auth0RedirectUri: string;

  public readonly version: string;
  public readonly defaultDateFormat: string;
  public readonly defaultDateFormatWithTime: string;
  public readonly defaultFilterDateRangeMonths: number;
  /** Default page size for client-side table pagination. */
  public readonly defaultTablePageSize: number;
  /** Fraction digits for line/unit cost display (ceil). */
  public readonly costFractionDigits: number;
  /** Fraction digits for aggregate total cost display (ceil). */
  public readonly totalCostFractionDigits: number;

  public readonly sentryDsn: string;
  public readonly sentryProject: string;
  public readonly sentryEnvironment: Environment;
  public readonly currentLogger: string;
  public readonly logLevel: LogLevel;
  public readonly sentryServiceName: string;

  private constructor() {
    this.graphqlBaseUrl = import.meta.env.VITE_GRAPHQL_BASE_URL ?? "/graphql/v1";
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
    this.auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
    this.auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";
    this.auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE ?? "";
    this.auth0RedirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI ?? window.location.origin;

    this.version = import.meta.env.VITE_APP_VERSION ?? "0.1.0";
    this.defaultDateFormat = "DD MMM, YYYY";
    this.defaultDateFormatWithTime = "DD MMM, YYYY HH:mm";
    this.defaultFilterDateRangeMonths = 1;
    this.defaultTablePageSize = Number(import.meta.env.VITE_DEFAULT_TABLE_PAGE_SIZE) || 10;
    this.costFractionDigits = parseCostFractionDigits(import.meta.env.VITE_COST_FRACTION_DIGITS, 4);
    this.totalCostFractionDigits = parseCostFractionDigits(
      import.meta.env.VITE_TOTAL_COST_FRACTION_DIGITS,
      2,
    );

    this.sentryDsn = import.meta.env.VITE_SENTRY_DSN ?? "";
    this.sentryProject = import.meta.env.VITE_SENTRY_PROJECT ?? "";
    this.sentryEnvironment = (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "DEV") as Environment;
    this.currentLogger = import.meta.env.VITE_CURRENT_LOGGER ?? "CONSOLE";
    this.logLevel = (import.meta.env.VITE_LOG_LEVEL ?? "info") as LogLevel;
    this.sentryServiceName = import.meta.env.VITE_SENTRY_SERVICE_NAME ?? "intelliextract-studio-web";
  }

  public static getInstance(): WebAppConfig {
    if (!WebAppConfig.instance) {
      WebAppConfig.instance = new WebAppConfig();
    }
    return WebAppConfig.instance;
  }

  /** @internal Vitest only — clears the singleton between env stubs. */
  public static resetInstanceForTests(): void {
    WebAppConfig.instance = undefined as unknown as WebAppConfig;
  }

  public bypassSessionAuth(): boolean {
    return import.meta.env.VITE_BYPASS_SESSION_AUTH === "true";
  }

  public isAuthConfigured(): boolean {
    if (this.bypassSessionAuth()) {
      return false;
    }
    return Boolean(this.auth0Domain && this.auth0ClientId);
  }

  public isProduction(): boolean {
    return this.sentryEnvironment === "PROD";
  }

  /** Show detailed errors in UI / allow console logging. */
  public exposeErrorDetails(): boolean {
    return !this.isProduction();
  }

  public isFeatureFlagEnabled(featureKey: StudioFeatureKey): boolean {
    const envKey = StudioFeatures.getFeatureEnvFlagKey(featureKey);
    const env = import.meta.env as Record<string, string | undefined>;
    const value = env[envKey];

    if (value === undefined || value.trim() === "") {
      return true;
    }

    return value.trim().toLowerCase() === "true";
  }
}
